import { useParams } from "react-router-dom";
import {
  useFetchRestaurantDetailsQuery,
  useUpdateRestaurantBannerMutation,
  useUpdateRestaurantLogoMutation,
  useUpdateRestaurantMutation,
} from "../store";
import InfoChip from "../components/common/InfoChip";
import { Severity } from "../types";
import RestaurantForm from "../forms/Restaurant.form";
import { showToast, ToastType } from "../utils/toast.utils";
import { HIDDEN_PASSWORd, restaurantSchema } from "../configs";
import ImageUploader from "../components/common/UploadImage";
import NavigationLinkBtn from "../components/common/NavigationLinkBtn";
import { FaArrowLeft } from "react-icons/fa";

const EditRestaurantWrapper = () => {
  const { restaurantId } = useParams();

  if (!restaurantId)
    return (
      <InfoChip
        severity={Severity.DANGER}
        content="Restaurant Introuvable"
      />
    );

  return <EditRestaurant restaurantId={restaurantId} />;
};

interface EditRestaurantProps {
  restaurantId: string;
}

const EditRestaurant: React.FC<EditRestaurantProps> = ({ restaurantId }) => {
  const [updateRestaurant, { isLoading }] = useUpdateRestaurantMutation();
  const [uploadLogo] = useUpdateRestaurantLogoMutation();
  const [uploadBanner] = useUpdateRestaurantBannerMutation();
  const { data: restaurantDetails, isFetching } =
    useFetchRestaurantDetailsQuery(restaurantId);

  if (isLoading || isFetching) return (
      <div className="flex justify-center items-center h-full w-full">
         <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
  );

  if (!restaurantDetails)
    return (
      <InfoChip
        severity={Severity.DANGER}
        content="Restaurant Introuvable"
      />
    );

  const handleUploadLogo = async (file: File) => {
    const result = await uploadLogo({ restaurantId, file });
    if (!result.error) {
      showToast("Logo mis à jour", ToastType.SUCCESS);
    }
  };

  const handleUploadBanner = async (file: File) => {
    const result = await uploadBanner({ restaurantId, file });
    if (!result.error) {
      showToast("Bannière mise à jour", ToastType.SUCCESS);
    }
  };

  const initialValues = {
    ...restaurantDetails,
    password: HIDDEN_PASSWORd,
  };

  return (
    <div className="w-full mx-auto px-6 py-8">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <NavigationLinkBtn
            to="/restaurants"
            label="<"
            className="bg-white text-gray-600 border border-gray-200 hover:bg-gray-50 p-3 rounded-full shadow-sm transition-all"
          />
          <div>
            <h1 className="text-2xl font-bold text-gray-800">
                Modifier Restaurant
            </h1>
            <p className="text-gray-500 text-sm mt-1">
                {restaurantDetails.displayName}
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100 flex flex-col items-center justify-center">
            <h3 className="text-gray-700 font-semibold mb-4 w-full text-left">Logo du restaurant</h3>
            <ImageUploader
            imgUrl={restaurantDetails.images?.logoPath}
            onUpload={handleUploadLogo}
            label="Changer le logo"
            showAction
            />
        </div>
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100 flex flex-col items-center justify-center">
            <h3 className="text-gray-700 font-semibold mb-4 w-full text-left">Bannière du restaurant</h3>
            <ImageUploader
            imgUrl={restaurantDetails.images?.bannerPath}
            onUpload={handleUploadBanner}
            label="Changer la bannière"
            showAction
            />
        </div>
      </div>

      <RestaurantForm
        initialValues={initialValues}
        validationSchema={restaurantSchema}
        isLoading={isLoading}
        onSubmit={async (values, { resetForm }) => {
          const body = { ...values };
          delete body.images;
          if (body.password === HIDDEN_PASSWORd) {
            delete body.password;
          }

          const result = await updateRestaurant({ restaurantId, body });

          if (!result.error) {
            showToast("Restaurant mis à jour", ToastType.SUCCESS);
            // Don't reset form on edit success to keep values
          }
        }}
      />
    </div>
  );
};

export default EditRestaurantWrapper;
